import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, FileText, Network, HardHat, Truck, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, downloadCsv, cx } from "@/lib/api";

const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const mondayOf = (s) => { const d = new Date(s + "T00:00:00"); return addDays(s, -((d.getDay() + 6) % 7)); };
const emptyA = () => ({ operai: [], mezzi: [], note: "" });

export default function ModelloOrganizzativo() {
  const [view, setView] = useState("giorno");
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [extra, setExtra] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // day view
  const [data, setData] = useState(todayISO());
  const [assign, setAssign] = useState({});
  // week view
  const [weekStart, setWeekStart] = useState(mondayOf(todayISO()));
  const [weekAssign, setWeekAssign] = useState({}); // {date: {cid: {operai,mezzi,note}}}
  const [editCell, setEditCell] = useState(null); // {date, cid}

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    const d = new Date(date + "T00:00:00");
    return { date, label: DOW[i], dom: d.getDate(), weekend: i >= 5 };
  });

  useEffect(() => {
    Promise.all([api.get("/cantieri"), api.get("/operai"), api.get("/mezzi")]).then(([c, o, m]) => {
      setCantieri(c.filter((x) => x.stato === "attivo"));
      setOperai(o); setMezzi(m);
    }).catch(() => {});
  }, []);

  const absorb = (it, ex) => {
    (it.operai_ids || []).forEach((id, i) => { ex[id] = (it.operai_nomi || [])[i]; });
    (it.mezzi_ids || []).forEach((id, i) => { ex[id] = (it.mezzi_nomi || [])[i]; });
  };

  const loadDay = useCallback(async () => {
    setLoading(true);
    try {
      const items = await api.get("/piano", { data });
      const map = {}; const ex = {};
      items.forEach((it) => { map[it.cantiere_id] = { operai: it.operai_ids || [], mezzi: it.mezzi_ids || [], note: it.note || "" }; absorb(it, ex); });
      setAssign(map); setExtra((e) => ({ ...e, ...ex }));
    } catch { toast.error("Caricamento piano non riuscito."); }
    setLoading(false);
  }, [data]);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const items = await api.get("/piano/range", { date_from: weekStart, date_to: addDays(weekStart, 6) });
      const map = {}; const ex = {};
      items.forEach((it) => {
        map[it.data] = map[it.data] || {};
        map[it.data][it.cantiere_id] = { operai: it.operai_ids || [], mezzi: it.mezzi_ids || [], note: it.note || "" };
        absorb(it, ex);
      });
      setWeekAssign(map); setExtra((e) => ({ ...e, ...ex }));
    } catch { toast.error("Caricamento settimana non riuscito."); }
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { if (view === "giorno") loadDay(); else loadWeek(); }, [view, loadDay, loadWeek]);

  const chipList = (base, picked) => {
    const ids = new Set(base.map((x) => x.id));
    const seen = new Set();
    const extras = (picked || []).filter((id) => !ids.has(id) && !seen.has(id) && seen.add(id))
      .map((id) => ({ id, nome: extra[id] || "—", inactive: true }));
    return [...base, ...extras];
  };

  // ----- day helpers -----
  const getA = (cid) => assign[cid] || emptyA();
  const toggle = (cid, kind, id) => setAssign((a) => {
    const cur = a[cid] || emptyA();
    const list = cur[kind].includes(id) ? cur[kind].filter((x) => x !== id) : [...cur[kind], id];
    return { ...a, [cid]: { ...cur, [kind]: list } };
  });
  const setNote = (cid, note) => setAssign((a) => ({ ...a, [cid]: { ...(a[cid] || emptyA()), note } }));

  const saveDay = async () => {
    const assegnazioni = cantieri.map((c) => { const a = getA(c.id); return { cantiere_id: c.id, operai_ids: a.operai, mezzi_ids: a.mezzi, note: a.note }; });
    setSaving(true);
    try { await api.post("/piano/bulk", { data, assegnazioni }); toast.success("Modello salvato."); loadDay(); }
    catch (err) { toast.error(err.message || "Salvataggio non riuscito."); }
    setSaving(false);
  };

  // ----- week helpers -----
  const getWA = (date, cid) => (weekAssign[date] && weekAssign[date][cid]) || emptyA();
  const toggleW = (date, cid, kind, id) => setWeekAssign((w) => {
    const day = { ...(w[date] || {}) };
    const cur = day[cid] || emptyA();
    const list = cur[kind].includes(id) ? cur[kind].filter((x) => x !== id) : [...cur[kind], id];
    day[cid] = { ...cur, [kind]: list };
    return { ...w, [date]: day };
  });
  const setNoteW = (date, cid, note) => setWeekAssign((w) => {
    const day = { ...(w[date] || {}) };
    day[cid] = { ...(day[cid] || emptyA()), note };
    return { ...w, [date]: day };
  });

  const saveWeek = async () => {
    setSaving(true);
    try {
      await Promise.all(weekDays.map(({ date }) => {
        const dayMap = weekAssign[date] || {};
        const assegnazioni = cantieri.map((c) => { const a = dayMap[c.id] || emptyA(); return { cantiere_id: c.id, operai_ids: a.operai, mezzi_ids: a.mezzi, note: a.note }; });
        return api.post("/piano/bulk", { data: date, assegnazioni });
      }));
      toast.success("Settimana salvata.");
      loadWeek();
    } catch (err) { toast.error(err.message || "Salvataggio non riuscito."); }
    setSaving(false);
  };

  const exportPdf = async () => {
    try {
      if (view === "giorno") await downloadCsv("/piano/export-pdf", `modello_${data}.pdf`, { data });
      else await downloadCsv("/piano/export-pdf-week", `modello_settimana_${weekStart}.pdf`, { start: weekStart });
      toast.success("PDF generato.");
    } catch { toast.error("Esportazione PDF non riuscita."); }
  };

  const Chips = ({ base, picked, onToggle, color, testPrefix }) => (
    <div className="flex flex-wrap gap-1.5">
      {chipList(base, picked).map((o) => (
        <button key={o.id} type="button" data-testid={`${testPrefix}-${o.id}`} onClick={() => onToggle(o.id)}
          className={cx("rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            picked.includes(o.id) ? `${color} text-white` : "border-border text-muted-foreground hover:border-slate-400")}>
          {o.nome}{o.inactive ? " ⚠" : ""}
        </button>
      ))}
    </div>
  );

  const nameOf = (id) => (operai.find((o) => o.id === id) || mezzi.find((m) => m.id === id) || { nome: extra[id] })?.nome || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Network className="h-7 w-7 text-amber-600" /> Modello Organizzativo</h1>
          <p className="text-sm text-muted-foreground mt-1">Organizza dove vanno gli operai e quali mezzi usano in cantiere, giorno per giorno.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf} data-testid="piano-export-pdf-button" className="gap-2"><FileText className="h-4 w-4" /> Esporta PDF</Button>
          <Button onClick={view === "giorno" ? saveDay : saveWeek} disabled={saving} data-testid="piano-save-button"
            className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border p-0.5">
          {[["giorno", "Giorno"], ["settimana", "Settimana"]].map(([k, l]) => (
            <button key={k} data-testid={`piano-view-${k}`} onClick={() => setView(k)}
              className={cx("rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                view === k ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-900" : "text-muted-foreground hover:text-foreground")}>
              {l}
            </button>
          ))}
        </div>
        {view === "giorno" ? (
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="piano-data-input" className="w-[180px]" />
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" data-testid="piano-week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium min-w-[180px] text-center">{new Date(weekStart + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – {new Date(addDays(weekStart, 6) + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}</span>
            <Button variant="outline" size="icon" className="h-9 w-9" data-testid="piano-week-next" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" data-testid="piano-week-today" onClick={() => setWeekStart(mondayOf(todayISO()))}>Oggi</Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-60 rounded-xl bg-secondary animate-pulse" />
      ) : cantieri.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium">Nessun cantiere aperto</p>
          <p className="text-sm text-muted-foreground">Apri un cantiere per pianificare le assegnazioni.</p>
        </CardContent></Card>
      ) : view === "giorno" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="piano-grid">
          {cantieri.map((c) => {
            const a = getA(c.id);
            return (
              <Card key={c.id} data-testid={`piano-cantiere-${c.id}`}>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> {c.nome}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><HardHat className="h-3.5 w-3.5" /> Operai ({a.operai.length})</p>
                    <Chips base={operai} picked={a.operai} onToggle={(id) => toggle(c.id, "operai", id)} color="bg-emerald-500 border-emerald-600" testPrefix={`piano-operaio-${c.id}`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Mezzi ({a.mezzi.length})</p>
                    <Chips base={mezzi} picked={a.mezzi} onToggle={(id) => toggle(c.id, "mezzi", id)} color="bg-sky-500 border-sky-600" testPrefix={`piano-mezzo-${c.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Note</Label>
                    <Textarea rows={2} value={a.note} onChange={(e) => setNote(c.id, e.target.value)} placeholder="Es. carico materiale, orario..." data-testid={`piano-note-${c.id}`} className="mt-1 text-sm" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card data-testid="piano-week-grid">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-card border-b border-r px-3 py-2 text-left font-medium min-w-[150px]">Cantiere</th>
                  {weekDays.map((d) => (
                    <th key={d.date} className={cx("border-b px-2 py-2 text-center font-medium min-w-[140px]", d.weekend && "bg-secondary/60")}>
                      {d.label} <span className="text-muted-foreground">{d.dom}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cantieri.map((c) => (
                  <tr key={c.id} className="align-top">
                    <td className="sticky left-0 z-10 bg-card border-b border-r px-3 py-2 font-medium whitespace-nowrap">{c.nome}</td>
                    {weekDays.map((d) => {
                      const a = getWA(d.date, c.id);
                      const filled = a.operai.length || a.mezzi.length || a.note;
                      return (
                        <td key={d.date} className={cx("border-b p-1", d.weekend && "bg-secondary/30")}>
                          <button data-testid={`piano-cell-${c.id}-${d.date}`} onClick={() => setEditCell({ date: d.date, cid: c.id })}
                            className={cx("w-full rounded-md border p-2 text-left text-xs transition-colors min-h-[48px]",
                              filled ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 hover:border-amber-400" : "border-dashed border-border text-muted-foreground hover:bg-secondary")}>
                            {!filled ? <span className="opacity-60">+ aggiungi</span> : (
                              <div className="space-y-0.5">
                                {a.operai.length > 0 && <p className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><HardHat className="h-3 w-3" /> {a.operai.map(nameOf).join(", ")}</p>}
                                {a.mezzi.length > 0 && <p className="flex items-center gap-1 text-sky-700 dark:text-sky-400"><Truck className="h-3 w-3" /> {a.mezzi.map(nameOf).join(", ")}</p>}
                                {a.note && <p className="italic text-muted-foreground truncate">{a.note}</p>}
                              </div>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editCell} onOpenChange={(o) => !o && setEditCell(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="piano-cell-dialog">
          {editCell && (() => {
            const c = cantieri.find((x) => x.id === editCell.cid);
            const a = getWA(editCell.date, editCell.cid);
            const dlabel = new Date(editCell.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{c?.nome}</DialogTitle>
                  <DialogDescription className="capitalize">{dlabel}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><HardHat className="h-3.5 w-3.5" /> Operai ({a.operai.length})</p>
                    <Chips base={operai} picked={a.operai} onToggle={(id) => toggleW(editCell.date, editCell.cid, "operai", id)} color="bg-emerald-500 border-emerald-600" testPrefix={`piano-dlg-operaio`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Mezzi ({a.mezzi.length})</p>
                    <Chips base={mezzi} picked={a.mezzi} onToggle={(id) => toggleW(editCell.date, editCell.cid, "mezzi", id)} color="bg-sky-500 border-sky-600" testPrefix={`piano-dlg-mezzo`} />
                  </div>
                  <div>
                    <Label className="text-xs">Note</Label>
                    <Textarea rows={2} value={a.note} onChange={(e) => setNoteW(editCell.date, editCell.cid, e.target.value)} data-testid="piano-dlg-note" className="mt-1 text-sm" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => setEditCell(null)} data-testid="piano-dlg-done"
                    className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">Fatto</Button>
                </DialogFooter>
                <p className="text-[11px] text-muted-foreground text-center">Ricordati di premere "Salva" per confermare la settimana.</p>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
